import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import styles from './MicrositePage.module.scss';
import { authFetch, openProtectedPdf } from '../auth/authFetch';

interface DocumentInfo {
  available: boolean;
  lastModified: string | null;
  url: string;
}

interface DocsAvailability {
  qualifications: DocumentInfo;
  leaderboard: DocumentInfo;
}

function MicrositePage() {
  const { instance, accounts } = useMsal();
  const [docsInfo, setDocsInfo] = useState<DocsAvailability | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [message, setMessage] = useState('');

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  /**
   * PDFs are served from a protected route, so they cannot be opened with a bare
   * window.open — that request would carry no Authorization header. The file is
   * fetched with a bearer token and then opened as an object URL.
   */
  const downloadPdf = async (fileName: string) => {
    setMessage('');
    try {
      await openProtectedPdf(fileName);
    } catch (error) {
      setMessage(`Could not open ${fileName}: ${error}`);
    }
  };

  useEffect(() => {
    // The server decides admin status from validated token claims. This only
    // controls whether the Admin Panel tile renders; the API enforces the rule.
    authFetch('/api/admin/me')
      .then(res => (res.ok ? res.json() : null))
      .then(data => setIsAdmin(data?.isAdmin === true))
      .catch(err => console.error('Error checking admin status:', err));

    authFetch('/api/docsinfo/available')
      .then(res => (res.ok ? res.json() : null))
      .then(data => setDocsInfo(data))
      .catch(err => console.error('Error fetching document availability:', err));
  }, [accounts]);

  return (
    <div className={styles.container}>
      <header className="header">
        <div className="wrapper">
          <div className="logo">
            <img src="/images/2025/1095 - Circle of Excellence_Colored_White_300x72.png" alt="Circle of Excellence" />
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
            <span style={{color: '#666', fontSize: '14px'}}>
              Logged in as: {accounts[0]?.username || accounts[0]?.name || 'User'}
            </span>
            <button onClick={handleLogout} style={{background: 'none', border: 'none', color: '#666', cursor: 'pointer'}}>
              Sign Out
            </button>
          </div>
        </div>
      </header>
      
      <section className={`intro ${styles.content}`}>
        <div className="wrapper">
          <h1 className={styles.title}>
            Employee Portal
          </h1>
          <p className={styles.subtitle}>
            Access your Circle of Excellence documents and standings
          </p>

          {message && (
            <div style={{ padding: '8px', margin: '8px 0', backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '13px' }}>
              {message}
            </div>
          )}

          <div className="cards-container">
            <div className="card">
              <h3>Qualifications</h3>
              <p>Learn more about Circle of Excellence qualifying criteria and program rules.</p>
              <button onClick={() => downloadPdf('Qualifications.pdf')} className="cta">
                <span>View Rules &amp; Qualifications</span> →
              </button>
            </div>
            
            <div className="card">
              <h3>Current Standings</h3>
              <p>Qualifying period: January 1, 2026 to December 31, 2026</p>
              <button onClick={() => downloadPdf('Leaderboard.pdf')} className="cta" style={{whiteSpace: 'nowrap'}}>
                <span>See Where You Stand</span> →
              </button>
              {docsInfo?.leaderboard?.lastModified && (
                <p style={{ fontSize: '12px', color: '#666', margin: '5px 0 0 0' }}>
                  Updated: {new Date(docsInfo.leaderboard.lastModified).toLocaleDateString()}
                </p>
              )}
            </div>
            
            {isAdmin && (
              <div className="card">
                <h3>Administration</h3>
                <p>Upload and manage Circle of Excellence documents.</p>
                <button onClick={() => window.location.href = '/admin'} className="cta">
                  <span>Admin Panel</span> →
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="wrapper">
          <div className="legal">&copy; {new Date().getFullYear()} Infor. All rights reserved.</div>
          <div className="brand">Designed and built by Infor Marketing</div>
        </div>
      </footer>
    </div>
  );
}

export default MicrositePage;
